'use client';

import React from 'react';
import {
  faXmark,
  faUserDoctor,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { PulseIconButton } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import {
  useGetProviderByIdQuery,
  useGetEntityProfileConfigQuery,
  useGetProviderStrategyTabsQuery,
} from 'src/api/redux';
import Link from 'next/link';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { getProfileUrl } from 'src/utils/profileUrl';
import { OldProfileProvider } from 'src/context/providers/ProfileProvider';
import { WidgetGrid } from 'src/components/Profiles/shared/widgets/WidgetGrid';
import 'src/components/Profiles/shared/profileWidgetConfig';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { TARGET_STRATEGIES_READ } from 'src/components/PermissionGuard/checks';
import StrategyDots from './StrategyDots';

interface TopProviderDetailPanelProps {
  provider: Record<string, any>;
  onClose: () => void;
}

/** Widget codes to skip entirely (carousels / interactive sections) */
const SKIP_WIDGET_CODES = new Set([
  'plays',
  'top_providers_at_site',
  'top_locations',
]);

const TopProviderDetailPanel: React.FC<TopProviderDetailPanelProps> = ({
  provider,
  onClose,
}) => {
  const { closeDrawer } = useDrawer();
  const providerId = provider.provider_id;

  const { data: entityDetails, isLoading: isEntityLoading } =
    useGetProviderByIdQuery({ providerId }, { skip: !providerId });

  const { data: profileConfig, isLoading: isConfigLoading } =
    useGetEntityProfileConfigQuery(
      { entityId: providerId },
      { skip: !providerId }
    );

  const { hasPermission } = useAuthorization();
  const { data: strategyData } = useGetProviderStrategyTabsQuery(
    { providerId },
    { skip: !providerId || !hasPermission(TARGET_STRATEGIES_READ) }
  );

  const strategyCount = strategyData?.count ?? 0;
  const { profileType } = getEntityIdentifiers(entityDetails);

  const widgets = (profileConfig?.widgets ?? []).filter(
    (w) => !SKIP_WIDGET_CODES.has(w.code)
  );

  const isLoading = isEntityLoading || isConfigLoading;

  return (
    <div
      className="absolute inset-0 z-10 flex flex-col bg-neutral-50 animate-slide-in-right"
      data-testid={`top-provider-detail-panel-${providerId}`}
      role="dialog"
      aria-label={`${provider.name} details`}
    >
      {/* Header */}
      <div className="flex items-center justify-between rounded-lg p-4 border-b border-neutral-200 bg-white">
        <div className="flex flex-col min-w-0">
          <p
            className="text-base font-bold text-neutral-800 truncate"
            data-testid="top-provider-detail-panel-name"
          >
            {provider.name}
          </p>
          <div className="flex items-center gap-2 text-xs text-neutral-600">
            {provider.specialty && (
              <span className="flex items-center gap-1">
                <Icon icon={faUserDoctor} className="w-3 h-3" />
                {provider.specialty}
              </span>
            )}
            <StrategyDots
              count={strategyCount}
              data-testid="top-provider-detail-strategies"
            />
          </div>
        </div>
        <PulseIconButton
          icon={faXmark}
          variant="plain"
          size="sm"
          onClick={onClose}
          aria-label="Close provider details"
          data-testid="top-provider-detail-panel-close"
        />
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {isLoading ? (
          <WidgetLoading data-testid="top-provider-detail-panel-loading" className="flex items-center justify-center py-12" />
        ) : entityDetails ? (
          <OldProfileProvider value={{ entityDetails }}>
            <div
              className="flex flex-col gap-4"
              data-testid="top-provider-detail-panel-widgets"
            >
              <WidgetGrid
                widgets={widgets}
                columns={1}
                testIdPrefix="top-provider-detail-panel-widget"
              />

              {/* View Full Profile button */}
              <Link
                href={getProfileUrl(profileType, providerId)}
                scroll={false}
                onClick={() => closeDrawer('profile')}
                className="flex items-center justify-center gap-2 rounded-full bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 transition-colors no-underline"
                data-testid="top-provider-detail-panel-view-profile"
              >
                View Full Profile
                <Icon icon={faArrowUpRightFromSquare} className="w-3 h-3" />
              </Link>
            </div>
          </OldProfileProvider>
        ) : (
          <WidgetEmpty data-testid="top-provider-detail-panel-empty" message="No profile data available" />
        )}
      </div>
    </div>
  );
};

export default TopProviderDetailPanel;
